import {StyleSheet, css} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {TabContainer, Tab} from 'ui-library/TabContainer';

import {PlateMapType} from '../annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import PlateMapDisplayLayerSidebar, {
  PickableLayersProps,
} from '../display-layer-controls/PlateMapDisplayLayerSidebar';
import {PlateRecord} from '../plate-record/PlateRecordConstants';
import {
  AssignmentGroup,
  AssignmentGroupValue,
  DisplayLayerOptionSection,
  PlateMap,
  PlateMapDisplayLayerAttribute,
} from '../PlateMapConstants';
import {ValidationErrorsByLayerAndLevel} from '../validation/ValidationTypes';
import {PlateRecordMetadata} from './PlateRecordMetadata';

interface PlateMapSidebarProps {
  plateRecord?: PlateRecord;

  /* Plate props */
  plateMap: PlateMap;
  selectedAttributeKey: string;
  setSelectedAttribute: (attr: PlateMapDisplayLayerAttribute) => void;
  assignmentGroups: {[attrKey: string]: {[id: string]: AssignmentGroup<AssignmentGroupValue>}};
  disabledSections: Array<DisplayLayerOptionSection>;
  /* Sections to be hidden entirely, rather than shown but disabled (as above). */
  hiddenSections?: Array<DisplayLayerOptionSection>;
  groupedValidationErrors: ValidationErrorsByLayerAndLevel;
  wellSchemaLayersProps: PickableLayersProps;
  plateMapType: PlateMapType;
  parameterLayersProps?: PickableLayersProps;
  additionalContent?: JSX.Element;
}

/**
 * Left sidebar for displaying metadata and display layers through a tabbed interface.
 * Will only show metadata tab if there are schema field values.
 */
export function PlateMapSidebar(props: PlateMapSidebarProps): JSX.Element {
  const {plateRecord, ...plateProps} = props;
  const [isOpen, setIsOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('plate');

  const hasPlateRecordSchemaFields = (plateRecord?.schemaFieldValues ?? []).length > 0;

  const content =
    plateRecord == null || !hasPlateRecordSchemaFields ? (
      <div
        className={classNames(
          'scrollbar-override',
          css(styles.sidebar),
          css(styles.tabsContainer),
          !isOpen && css(styles.tabsClosed)
        )}
        {...getTestProps({component: 'PlateMapDisplayLayerSidebar', element: 'root'})}
      >
        <PlateMapDisplayLayerSidebar {...plateProps} />
      </div>
    ) : (
      <TabContainer
        activeTab={activeTab}
        onRequestTabChange={setActiveTab}
        className={classNames(css(styles.tabsContainer), !isOpen && css(styles.tabsClosed))}
        paneClassName={css(styles.pane)}
      >
        <Tab tabKey="plate" title="PLATE">
          <div
            className={classNames('scrollbar-override', css(styles.sidebar))}
            {...getTestProps({component: 'PlateMapDisplayLayerSidebar', element: 'root'})}
          >
            <PlateMapDisplayLayerSidebar {...plateProps} />
          </div>
        </Tab>

        <Tab tabKey="metadata" title="METADATA">
          <div
            className={classNames('scrollbar-override', css(styles.sidebar))}
            {...getTestProps({component: 'PlateMapMetaData', element: 'root'})}
          >
            {plateRecord && <PlateRecordMetadata schemaFieldValues={plateRecord.schemaFieldValues} />}
          </div>
        </Tab>
      </TabContainer>
    );
  return (
    <div
      {...getTestProps({component: 'PlateMapSidebar', element: 'root'})}
      className={classNames(css(styles.sidebarWrapper), !isOpen && css(styles.closed), 'hide-on-print')}
    >
      {content}
      <Button
        buttonStyle={ButtonStyle.CIRCLE_ICON}
        className={css(styles.opener)}
        icon={isOpen ? 'chevron-left' : 'chevron-right'}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        size={ButtonSize.MEDIUM}
      />
    </div>
  );
}

const SIDEBAR_WIDTH = 240;
const SIDEBAR_PADDING = 16;

const styles = StyleSheet.create({
  pane: {
    height: 'calc(100% - 7px)',
    marginTop: 0,
  },
  tabsContainer: {
    display: 'flex',
    flexDirection: 'column',
    opacity: 1,
    transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in',
    width: SIDEBAR_WIDTH - SIDEBAR_PADDING,
    transform: 'translate(0px, 0px)',
    height: '100%',
  },
  tabsClosed: {
    opacity: 0,
    transform: `translate(-${SIDEBAR_WIDTH}px, 0px)`,
    width: SIDEBAR_WIDTH - SIDEBAR_PADDING,
    pointerEvents: 'none',
  },
  sidebarWrapper: {
    overflow: 'hidden',
    position: 'relative',
    width: SIDEBAR_WIDTH,
    transition: '0.3s ease-in-out',
  },
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: '20px',
    gap: 10,
    width: SIDEBAR_WIDTH,
    minWidth: SIDEBAR_WIDTH,
    paddingRight: SIDEBAR_PADDING,
    overflowY: 'auto',
    overflowX: 'hidden',
    height: '100%',
  },
  closed: {
    width: 20,
  },
  opener: {
    position: 'absolute',
    top: -10,
    right: 0,
  },
});
